import { Divider, Select, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { deduplicateComboboxGroups } from '../../../helpers/general-helpers';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import { setBoard, setTableSelectedYear } from '../../../store/financials-store/financials-store.actions';
import { Button } from '../../../ui-library/button/button';
import { getBoardOptions, useTeams, useWorkPeriodsData } from '../../work-periods/hooks/work-periods.hooks';
import { getAvailableSprintYears } from './by-date.helpers';
import { EditView } from '../financials';

function Filters({
  availableYears,
  editView,
  setEditView,
}: {
  availableYears: Set<number>;
  editView: EditView;
  setEditView: (view: EditView) => void;
}) {
  return (
    <Stack>
      <Select
        radius="xl"
        data={Object.values(EditView)}
        value={editView}
        onChange={(value) => setEditView(value as EditView)}
      />
      {editView === EditView.ByDate ? (
        <FiltersByDate availableYears={availableYears} />
      ) : (
        <FiltersBySprint availableYears={availableYears} />
      )}
    </Stack>
  );
}

function FiltersBySprint({ availableYears }: { availableYears: Set<number> }) {
  const { t } = useTranslation();
  const selectedYear = useFinancialsStore((state) => state.table.selectedYear);
  const selectedBoard = useFinancialsStore((state) => state.board);

  const teams = useTeams();
  const formattedBoards = useMemo(() => {
    const boardOptions = getBoardOptions(teams);
    const deduplicated = deduplicateComboboxGroups(boardOptions);
    return deduplicated.flatMap((group) =>
      group.items.map((item) => {
        const comboItem = typeof item === 'string' ? { value: item, label: item } : item;
        return {
          value: comboItem.value as string,
          label: comboItem.label as string,
        };
      }),
    );
  }, [teams]);

  useEffect(() => {
    if (formattedBoards.length > 0) {
      const isCurrentBoardValid =
        selectedBoard && formattedBoards.some((formattedBoard) => formattedBoard.value === selectedBoard);

      if (!selectedBoard || !isCurrentBoardValid) {
        const defaultBoard = formattedBoards[0];

        if (defaultBoard) {
          const boardChanged = selectedBoard !== defaultBoard.value;

          if (boardChanged) {
            setBoard(defaultBoard.value);
          }
        }
      }
    }
  }, [selectedBoard, formattedBoards]);

  const { data: workPeriods } = useWorkPeriodsData(selectedBoard);

  // Generate year options from work periods, falling back to spending-derived years
  const yearOptions = useMemo(
    () => getAvailableSprintYears(workPeriods, availableYears),
    [workPeriods, availableYears],
  );

  // Set default year to current year if available in options
  useEffect(() => {
    if (yearOptions.length > 0) {
      if (!selectedYear) {
        const currentYear = dayjs().year();
        const hasCurrentYear = yearOptions.some((option) => option.value === currentYear.toString());
        setTableSelectedYear(hasCurrentYear ? currentYear : Number(yearOptions[0].value));
      } else {
        // if year is not in yearOptions, set it to the latest year
        if (!yearOptions.some((option) => option.value === selectedYear.toString())) {
          setTableSelectedYear(Number(yearOptions[0].value));
        }
      }
    }
  }, [yearOptions, selectedYear]);

  return (
    <Stack>
      {selectedYear && (
        <Select
          radius="xl"
          data={yearOptions}
          value={selectedYear?.toString()}
          onChange={(value) => setTableSelectedYear(Number(value))}
        />
      )}
      <Text fw={750}>{t('financials.table.boards')}</Text>
      {formattedBoards.map((formattedBoard) => (
        <Button
          key={formattedBoard.value}
          variant={formattedBoard.value === selectedBoard ? 'secondary' : 'outline'}
          radius="xl"
          size="sm"
          onClick={() => {
            setBoard(formattedBoard.value);
          }}
        >
          {formattedBoard.label}
        </Button>
      ))}
    </Stack>
  );
}

function FiltersByDate({ availableYears }: { availableYears: Set<number> }) {
  const { t } = useTranslation();
  const selectedYear = useFinancialsStore((state) => state.table.selectedYear);
  const sortedAvailableYears = Array.from(availableYears).sort((a, b) => b - a);

  // set default year to the latest year if not selected
  useEffect(() => {
    if (sortedAvailableYears.length) {
      if (!selectedYear) {
        setTableSelectedYear(sortedAvailableYears[0]);
      } else {
        // if year is not in available years, set it to the latest year
        if (!sortedAvailableYears.some((year) => year === selectedYear)) {
          setTableSelectedYear(sortedAvailableYears[0]);
        }
      }
    }
  }, [selectedYear, sortedAvailableYears]);

  return (
    <Stack>
      <Text fw={750}>{t('financials.table.years')}</Text>
      <Divider />
      {(sortedAvailableYears || []).map((year) => (
        <Button
          key={year}
          variant={selectedYear === year ? 'secondary' : 'outline'}
          radius="xl"
          size="sm"
          style={{ width: '70px' }}
          onClick={() => setTableSelectedYear(year)}
        >
          {year}
        </Button>
      ))}
    </Stack>
  );
}

export { Filters };
